import React from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSystemsContext } from '../../context/SystemsContext';
import { getFormSectionStyles } from './forms/formSectionStyles';

const SystemActionButtons: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const styles = getFormSectionStyles(theme);

  const {
    isLoadingSystem,
    selectedSection,
    selectedConfig: expandedConfigId,
    dirty,
    canSave,
    saveChanges,
    cancelCreateSystem,
    deleteConfig,
    duplicateConfig,
    deleteSystem,
  } = useSystemsContext();

  if (isLoadingSystem) {
    return null;
  }

  const isServiceDefinitions = selectedSection === 'serviceDefinitions';
  const isConfigSectionSelected =
    !!expandedConfigId &&
    selectedSection !== 'systemInfo' &&
    selectedSection !== 'createSystem' &&
    !isServiceDefinitions;

  if (isServiceDefinitions) {
    return null;
  }

  const isCreate = selectedSection === 'createSystem';
  const saveLabel = isCreate ? 'Create system' : dirty ? 'Save changes' : 'All changes saved';

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        mt: 4,
        mx: { xs: -2, md: -3 },
        px: { xs: 2, md: 3 },
        py: 1.5,
        borderTop: `1px solid ${styles.surfaceBorder}`,
        backgroundColor: isDark ? alpha('#0b0b0d', 0.92) : alpha('#f4f4f6', 0.92),
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        zIndex: 2,
      }}
    >
      {/* Left: secondary / destructive actions */}
      <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
        {isCreate && (
          <Button
            data-cy="system-cancel-btn"
            onClick={cancelCreateSystem}
            sx={styles.ghostButton}
          >
            Cancel
          </Button>
        )}

        {selectedSection === 'systemInfo' && (
          <Button
            data-cy="system-delete-system-btn"
            onClick={deleteSystem}
            sx={styles.dangerGhostButton}
          >
            Delete system
          </Button>
        )}

        {isConfigSectionSelected && (
          <>
            <Button
              data-cy="system-duplicate-config-btn"
              onClick={duplicateConfig}
              sx={styles.ghostButton}
            >
              Duplicate config
            </Button>
            <Button
              data-cy="system-delete-config-btn"
              onClick={deleteConfig}
              sx={styles.dangerGhostButton}
            >
              Delete config
            </Button>
          </>
        )}
      </Box>

      {/* Right: save state + primary action */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {!isCreate && (
          <Typography
            variant="caption"
            sx={{
              color: dirty ? styles.accent : 'text.secondary',
              fontWeight: dirty ? 600 : 500,
              letterSpacing: '0.02em',
            }}
          >
            {dirty ? 'Unsaved changes' : 'Up to date'}
          </Typography>
        )}
        <Button
          data-cy="system-save-btn"
          onClick={saveChanges}
          disabled={!canSave}
          sx={{ ...styles.primaryButton, minWidth: 160 }}
        >
          {saveLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default SystemActionButtons;
